import { yupResolver } from '@hookform/resolvers/yup'
import { Box, FormControlLabel, Stack, Switch } from '@mui/material'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import { useProfileContext } from 'context/ProfileContext'
import { useAddEducation, useGetEducationById, useUpdateEducation } from 'hooks/useUsers'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { snackbarService } from 'services/snackbarService'
import * as yup from 'yup';
import { Dayjs } from 'dayjs';
import dayjs from 'lib/dayjs';
import { useAuth } from 'context/AuthContext'
import PlacesSearch from 'components/common/PlacesSearch/PlacesSearch'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect'
import CustomDatePicker from 'components/inputs/CustomDatePicker/CustomDatePicker'
import { useGetDegrees, useGetSpecialities } from 'hooks/useLookups'
import { fonts } from 'styles/fonts'


interface Props {
  open: boolean,
  setOpen: (value: boolean) => void;
  educationId?: number;
}

export default function EducationDialog({
  open,
  setOpen,
  educationId,
}: Props) {
  const { user } = useAuth();
  const { refetch } = useProfileContext();
  const { data: specialities } = useGetSpecialities();
  const { data: degrees } = useGetDegrees();
  const [checked, setChecked] = useState<boolean>(true);

  const getValidationSchema = () => {
    const baseSchema = {
      start_date: yup
        .mixed<Dayjs>()
        .nullable()
        .required('Start date is required'),
      institution_name: yup.string().required('Institution is required'),
      speciality_id: yup.string().required('Speciality is required'),
      degree_id: yup.string().required('Degree is required'),
    };

    return yup.object({
      ...baseSchema,
      end_date: yup
        .mixed<Dayjs>()
        .nullable()
        .optional(),
    });
  };

  const validationSchema = getValidationSchema();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      institution_name: '',
      start_date: undefined,
      end_date: undefined,
      speciality_id: '',
      degree_id: '',
    }
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
    setValue,
  } = methods;

  useGetEducationById({
    userId: user.id,
    educationId: educationId as number,
  }, {
    enabled: !!educationId,
    onSuccess: (data: any) => {

      const startDate = data.start_date ? dayjs(data.start_date.toISOString().split('T')[0]) : null;
      const endDate = data.end_date ? dayjs(data.end_date.toISOString().split('T')[0]) : null;

      reset({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        institution_name: data.institution_name,
        degree_id: data.degree.id.toString() || '',
        speciality_id: data.speciality.id.toString() || '',
      });

      setChecked(data.end_date ? false : true);
    },
    onError: (error: any) => {
      console.log('error', error);
    }
  })

  const { mutate: createMutation, isLoading: isCreating } = useAddEducation(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode === 200) {
        setOpen(false);
        snackbarService.showSnackbar(`Education has been added successfully`, 'success');
        refetch();
        resetForm();
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    (error: any) => { console.log('error', error) },
  )

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateEducation(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode === 200) {
        setOpen(false);
        snackbarService.showSnackbar(`Education has been updated successfully`, 'success');
        refetch();
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    (error: any) => { console.log('error', error) },
  )

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      start_date: data.start_date
        ? dayjs(data.start_date).format('MMM DD, YYYY')
        : null,
      end_date: data.end_date
        ? dayjs(data.end_date).format('MMM DD, YYYY')
        : null,
    };

    if (checked || !data.end_date) {
      delete payload.end_date;
      setValue('end_date', undefined);
    }

    if (educationId) {
      updateMutation({
        userId: user.id,
        educationId: educationId,
        dto: payload,
      });
    } else {
      createMutation({
        userId: user.id,
        dto: payload,
      });
    }
  };

  const resetForm = () => {
    reset({
      start_date: undefined,
      end_date: undefined,
      institution_name: '',
      speciality_id: '',
    });
  }

  return (
    <CustomDialog
      open={open}
      title={`${educationId ? 'Edit' : 'Add'} Education`}
      alignText='center'
      onClose={() => setOpen(false)}
      content={
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >

            <Box mt={3}>
              <PlacesSearch
                name="institution_name"
                control={control}
                onChange={(name) => {
                  setValue(
                    "institution_name",
                    name,
                    { shouldDirty: true, shouldValidate: true }
                  );
                }}
              />
            </Box>

            <Box mt={2}>
              <CustomFormSelect
                name={'speciality_id'}
                options={specialities || []}
                floatingLabel='Speciality'
                placeholder='Select speciality'
                required
              />
            </Box>

            <Box mt={2}>
              <CustomFormSelect
                name={'degree_id'}
                options={degrees || []}
                floatingLabel='Degree'
                placeholder='Select degree'
                required
              />
            </Box>

            <Box mt={2}>
              <CustomDatePicker
                name={'start_date'}
                placeholder='Start date'
                control={control}
                floatingLabel='Start date'
                required
              />
            </Box>

            <Box mt={2} pl={1}>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={checked}
                      onChange={() => setChecked(prev => !prev)}
                      size='small'
                    />}
                  label="I currently study"
                  slotProps={{
                    typography: {
                      sx: {
                        fontFamily: fonts.neue,
                        fontWeight: 500,
                        marginLeft: '5px',
                      }
                    }
                  }}
                />
              </Stack>
            </Box>

            {!checked && <Box mt={2}>
              <CustomDatePicker
                name={'end_date'}
                placeholder='End date'
                control={control}
                floatingLabel='End date'
                required
              />
            </Box>}

            <Box my={2}>
              <CustomButton
                text={`${educationId ? 'Save' : 'Add'}`}
                color='primary'
                fullWidth
                type='submit'
                size='large'
                loading={isCreating || isUpdating}
                disabled={!isValid}
              />
            </Box>

          </Box>
        </FormProvider>
      }
    />
  )
}
